import React, { useState, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MagnifyingGlass, ArrowClockwise, CheckCircle, Warning, X, Package } from '@phosphor-icons/react';

const COMPARE_LABELS = {
  forme_embout: "Forme", embout_type: "Type d'embout", matiere: "Matière", material: "Matière",
  durete: "Dureté", etancheite: "Étanchéité", finition: "Finition", marque: "Marque", modele: "Modèle",
  ecouteur_version: "Version écouteur", ecouteur_puissance: "Puissance", ecouteur_type: "Type écouteur",
  ecouteur_puissance_oticon: "Puissance Oticon", event_od: "Event OD", event_og: "Event OG", color: "Couleur",
};

export default function RefabSearchBlock({ onSelectOrder, onClear, selectedOriginal }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const search = useCallback(async () => {
    if (!query.trim() || query.trim().length < 2) return;
    setSearching(true);
    setSearched(true);
    try {
      const r = await axios.get(`${API}/orders/search-refab?q=${encodeURIComponent(query.trim())}`);
      setResults(r.data);
    } catch { setResults([]); }
    finally { setSearching(false); }
  }, [query]);

  const handleKeyDown = (e) => { if (e.key === 'Enter') { e.preventDefault(); search(); } };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  if (selectedOriginal) {
    const o = selectedOriginal;
    return (
      <div className="border-2 border-amber-400 bg-amber-50 rounded-sm p-4" data-testid="refab-selected-block">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ArrowClockwise size={20} weight="bold" className="text-amber-700" />
            <span className="font-heading font-bold text-sm text-amber-900 uppercase tracking-wider">Refabrication</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClear} className="text-xs text-zinc-500 h-7" data-testid="refab-clear-btn">
            <X size={14} className="mr-1" />Annuler
          </Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
          <div><span className="text-amber-700 font-medium">Patient:</span> {o.patient_reference}</div>
          <div><span className="text-amber-700 font-medium">Client:</span> {o.client_name}</div>
          <div><span className="text-amber-700 font-medium">Date:</span> {formatDate(o.created_at)}</div>
          <div>
            {o.refab_eligible_free
              ? <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-sm font-bold">Éligible gratuit</span>
              : <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-sm font-bold">&gt; 365 jours</span>}
          </div>
          {o.serial_number_left && <div><span className="text-amber-700 font-medium">N° OG:</span> {o.serial_number_left}</div>}
          {o.serial_number_right && <div><span className="text-amber-700 font-medium">N° OD:</span> {o.serial_number_right}</div>}
          <div><span className="text-amber-700 font-medium">Produit:</span> {o.product_name}</div>
          <div><span className="text-amber-700 font-medium">Matière:</span> {o.material}</div>
        </div>
        <p className="text-xs text-amber-700 mt-2 italic">Les champs du formulaire ont été préremplis. Modifiez uniquement ce qui change.</p>
      </div>
    );
  }

  return (
    <div className="border-2 border-dashed border-amber-300 bg-amber-50/50 rounded-sm p-4" data-testid="refab-search-block">
      <div className="flex items-center gap-2 mb-3">
        <ArrowClockwise size={20} weight="bold" className="text-amber-700" />
        <span className="font-heading font-bold text-sm text-amber-900 uppercase tracking-wider">Refabrication</span>
        <span className="text-xs text-amber-600 ml-auto">Rechercher la commande d'origine</span>
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-amber-500" size={16} />
          <Input
            placeholder="N° série ou référence patient..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="pl-9 rounded-sm border-amber-300 bg-white"
            data-testid="refab-search-input"
          />
        </div>
        <Button onClick={search} disabled={searching || query.trim().length < 2} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm" data-testid="refab-search-btn">
          {searching ? 'Recherche...' : 'Rechercher'}
        </Button>
      </div>

      {searched && results.length === 0 && !searching && (
        <p className="text-sm text-zinc-500 mt-3 text-center">Aucun résultat trouvé</p>
      )}

      {results.length > 0 && (
        <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
          {results.map(order => (
            <button
              key={order.id}
              onClick={() => { onSelectOrder(order); setResults([]); setQuery(''); setSearched(false); }}
              className="w-full text-left border border-amber-200 rounded-sm p-3 hover:bg-amber-100 transition-colors"
              data-testid={`refab-result-${order.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package size={16} className="text-amber-700" />
                  <span className="font-medium text-sm">{order.patient_reference}</span>
                  <span className="text-xs text-zinc-500">{order.client_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {order.refab_eligible_free
                    ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-sm font-medium">Gratuit</span>
                    : <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-sm font-medium">Payant</span>}
                  <span className="text-xs text-zinc-400">{formatDate(order.created_at)}</span>
                </div>
              </div>
              <div className="flex gap-4 mt-1 text-xs text-zinc-500">
                {order.serial_number_left && <span>OG: {order.serial_number_left}</span>}
                {order.serial_number_right && <span>OD: {order.serial_number_right}</span>}
                <span>{order.product_name}</span>
                <span>{order.material === 'resine_3d' ? 'Résine 3D' : order.material === 'silicone' ? 'Silicone' : order.material}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Compute changed fields between original order and current form state */
export function computeRefabChanges(originalOrder, currentOptions, currentMaterial, currentColor) {
  if (!originalOrder) return [];
  const changes = [];
  const origOpts = originalOrder.options || {};

  const fields = [
    ['forme_embout', 'Forme'], ['embout_type', "Type d'embout"],
    ['durete', 'Dureté'], ['etancheite', 'Étanchéité'], ['finition', 'Finition'],
    ['marque', 'Marque'], ['modele', 'Modèle'],
    ['ecouteur_version', 'Version écouteur'], ['ecouteur_puissance', 'Puissance'],
    ['ecouteur_type', 'Type écouteur'], ['ecouteur_puissance_oticon', 'Puissance Oticon'],
    ['event_od', 'Event OD'], ['event_og', 'Event OG'],
  ];

  for (const [key, label] of fields) {
    const oldVal = String(origOpts[key] || '').trim();
    const newVal = String(currentOptions[key] || '').trim();
    if (oldVal !== newVal && (oldVal || newVal)) {
      changes.push({ field: key, label, old: oldVal, new: newVal });
    }
  }

  // Compare top-level
  if (currentMaterial && currentMaterial !== (originalOrder.material || '')) {
    changes.push({ field: 'material', label: 'Matière', old: originalOrder.material || '', new: currentMaterial });
  }
  if (currentColor && currentColor !== (originalOrder.color || '')) {
    changes.push({ field: 'color', label: 'Couleur', old: originalOrder.color || '', new: currentColor });
  }

  return changes;
}

/** Get the set of changed field keys for red highlighting */
export function getChangedFieldKeys(changes) {
  return new Set(changes.map(c => c.field));
}
